import {useCallback, useEffect, useState} from "react";
import {useOutletContext} from "react-router";
import {Grid2 as Grid, Paper} from "@mui/material";
import {apiGet} from "../utils";
import {appendQueryString, buildV2QueryParams, hasDateFilters} from "../utils/filters";
import {LoadingPanel} from "../utils/loading_spinner";
import {InsightsPanel} from "../utils/panels";
import {ProfitLeaksSummary} from "../components/ProfitLeaksSummary";
import {ProfitLeaksTable} from "../components/ProfitLeaksTable";

export const ProfitLeaksContainer = () => {
    const {filters} = useOutletContext();
    const [summaryData, setSummaryData] = useState([]);
    const [selectedLeak, setSelectedLeak] = useState(null);
    const [detailData, setDetailData] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 25, total: 0 });
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    const fetchSummaryData = useCallback(() => {
        if (!hasDateFilters(filters)) return;

        setLoading(true);
        const queryParams = buildV2QueryParams(filters);
        apiGet(`v2/profit-leaks/summary${queryParams}`, (resp) => {
            if (resp?.success) {
                setSummaryData(resp.data);
            }
            setLoading(false);
        });
    }, [filters]);

    const fetchProfitLeaksData = (id, page = 0, limit = 25) => {
        if (!hasDateFilters(filters)) return;
        setDetailLoading(true);
        const queryParams = buildV2QueryParams(filters);
        const fullParams = appendQueryString(`v2/profit-leaks/detail/${id}${queryParams}`, `page=${page}&limit=${limit}`);
        apiGet(fullParams, (resp) => {
            if (resp?.success) {
                setDetailData(resp.data.list);
                setPagination(resp.data.pagination);
            }
            setDetailLoading(false);
        });
    };

    useEffect(() => {
        fetchSummaryData();
    }, [fetchSummaryData]);

    const handleRowSelect = (leak) => {
        setPagination({ page: 0, limit: pagination.limit, total: 0 });
        fetchProfitLeaksData(leak.id, 0, pagination.limit);
        setSelectedLeak(leak);
    };

    const handlePageChange = (newPage) => {
        if (selectedLeak) {
            fetchProfitLeaksData(selectedLeak.id, newPage, pagination.limit);
        }
    };

    const handlePageSizeChange = (newLimit) => {
        if (selectedLeak) {
            setPagination({ page: 0, limit: newLimit, total: pagination.total });
            fetchProfitLeaksData(selectedLeak.id, 0, newLimit);
        }
    };

    if (loading) {
        return <LoadingPanel loading={true} message="Loading profit leaks analysis..." />;
    }

    return (
        <div style={{margin: 30}}>
            <Grid container spacing={2}>
                <Grid size={9}>
                    <ProfitLeaksSummary
                        data={summaryData}
                        onRowSelect={handleRowSelect}
                        selectedId={selectedLeak?.id}
                    />
                </Grid>
                <Grid size={3}>
                    <Paper sx={{
                        backgroundColor: 'white',
                        border: 'thin solid #323D7225',
                        borderRadius: 2,
                        boxShadow: '1px 1px #323D7250',
                        p: 2
                    }}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <InsightsPanel
                                    is_insight={true}
                                    content={selectedLeak
                                        ? selectedLeak.insights || `Analysis for ${selectedLeak.description}: Contribution margin/unit decreased by $25/unit over the past 90 days. Estimated margin leak is $620K`
                                        : 'Select a profit leak from the table to view insights'}
                                />
                            </Grid>
                            <Grid size={12}>
                                <InsightsPanel
                                    is_insight={false}
                                    content={selectedLeak
                                        ? selectedLeak.rootCause || 'Most of the decrease is driven by sales overrides outside approved bands.'
                                        : 'Select a profit leak from the table to view root cause analysis'}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            <ProfitLeaksTable
                loading={detailLoading}
                data={detailData}
                selectedLeak={selectedLeak}
                pagination={pagination}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
            />
        </div>
    );
};
